import { Temporal } from '@js-temporal/polyfill';
import { month, monthIndex } from './dates.js';

/**
 * A rough time a customer gives for paying ("this week", "in a couple of days", "first week of December"), read the way a
 * collector would: the last business day it can mean. `date` is null when the phrase cannot be read as an upcoming day
 * (the first week of a month already gone, mid-month said after the 15th). The date is approximate: the acknowledgement
 * states it so the customer can correct it.
 */
export interface ApproximateMention { phrase: string; index: number; end: number; date: string | null }

type Day = Temporal.PlainDate;
/** This week's Friday; on a weekend, the coming Friday ("this week" said on Saturday means the week ahead). */
function thisFriday(base: Day): Day { return base.add({ days: base.dayOfWeek <= 5 ? 5 - base.dayOfWeek : 12 - base.dayOfWeek }); }
/** The Friday of the week that starts on the next Monday. */
function nextFriday(base: Day): Day { return base.add({ days: 8 - base.dayOfWeek + 4 }); }
/** `count` business days (Monday to Friday) after `base`. */
export function addBusinessDays(base: Day, count: number): Day {
  let day = base;
  for (let left = count; left > 0;) { day = day.add({ days: 1 }); if (day.dayOfWeek <= 5) left--; }
  return day;
}
/** The first day of a named month that is still ahead (or the current month), as `dateMentions` reads a yearless month. */
function upcomingMonth(base: Day, name: string): Day {
  const number = monthIndex(name);
  return Temporal.PlainDate.from({ year: number < base.month ? base.year + 1 : base.year, month: number, day: 1 });
}
const firstFriday = (first: Day) => first.add({ days: (5 - first.dayOfWeek + 7) % 7 });

const readers: [RegExp, (base: Day, match: RegExpMatchArray) => Day | null][] = [
  [/\b(?:by |before )?(?:the )?end of next week\b|\b(?:sometime |early |later? )?next week\b/g, base => nextFriday(base)],
  [/\b(?:by |before )?(?:the )?end of (?:the |this )?week\b|\b(?:sometime |later )?this week\b/g, base => thisFriday(base)],
  [/\b(?:in|within) (?:a |the next )?(?:couple(?: of)?|two|2) (?:business |working )?days\b|\bin a day or two\b/g,
    base => addBusinessDays(base, 2)],
  [/\b(?:in|within) (?:a |the next )?(?:few|three|3) (?:business |working )?days\b/g, base => addBusinessDays(base, 3)],
  [new RegExp(String.raw`\bfirst week of (next month|${month})\b`, 'g'), (base, match) => {
    const first = match[1] === 'next month' ? base.add({ months: 1 }).with({ day: 1 }) : upcomingMonth(base, match[1]!);
    return firstFriday(first);
  }],
  [new RegExp(String.raw`\b(?:mid[- ]?|the middle of )(month|${month})\b`, 'g'), (base, match) =>
    (match[1] === 'month' ? base : upcomingMonth(base, match[1]!)).with({ day: 15 })],
  [new RegExp(String.raw`\b(?:end of (?:the |this )?month|month[- ]end|eom|end of next month|end of (${month}))\b`, 'g'), (base, match) => {
    if (match[0].includes('next month')) { const next = base.add({ months: 1 }); return next.with({ day: next.daysInMonth }); }
    const start = match[1] ? upcomingMonth(base, match[1]) : base;
    return start.with({ day: start.daysInMonth });
  }],
];

/** Every approximate time phrase in the text, resolved against the day the reply was received. */
export function approximateMentions(text: string, receivedOn: string): ApproximateMention[] {
  const base = Temporal.PlainDate.from(receivedOn); const lower = text.toLowerCase();
  const found: ApproximateMention[] = [];
  for (const [pattern, read] of readers) {
    for (const match of lower.matchAll(pattern)) {
      const index = match.index; const end = index + match[0].length;
      if (found.some(f => index < f.end && end > f.index)) continue;
      let day: Day | null; try { day = read(base, match); } catch { day = null; }
      const date = day && Temporal.PlainDate.compare(day, base) >= 0 ? day.toString() : null;
      found.push({ phrase: match[0].trim(), index, end, date });
    }
  }
  return found.sort((a, b) => a.index - b.index);
}
